import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import { API_BASE } from '../lib/config';
import {
  UsersIcon,
  BriefcaseIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  BellIcon,
  CogIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  SparklesIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  HomeIcon,
  KeyIcon,
  IdentificationIcon,
  AcademicCapIcon,
  TagIcon,
  CurrencyDollarIcon,
  WalletIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

// Components
import DashboardOverview from '../components/admin/DashboardOverview';
import UserManagement from '../components/admin/UserManagement';
import JobManagement from '../components/admin/JobManagement';
import QualificationManagement from '../components/admin/QualificationManagement';

import DataExport from '../components/admin/DataExport';
import AbuseReports from '../components/admin/AbuseReports';
import NotificationManagement from '../components/admin/NotificationManagement';
import BannerManagement from '../components/admin/BannerManagement';
import APIKeyManagement from '../components/admin/APIKeyManagement';
import SystemSettings from '../components/admin/SystemSettings';
import Analytics from '../components/admin/Analytics';
import SecurityManagement from '../components/admin/SecurityManagement';
import OptionalFeatures from '../components/admin/OptionalFeatures';
import CategoryManagement from '../components/admin/CategoryManagement';
import ContentEditManagement from '../components/admin/ContentEditManagement';
import IdentityVerificationManagement from '../components/admin/IdentityVerificationManagementNew';
import AdminWalletManagement from '../components/admin/AdminWalletManagement';
import PaymentManagement from '../components/admin/PaymentManagement';
import QuickNewUsers from '../components/admin/QuickNewUsers';


import QuickJobApprovals from '../components/admin/QuickJobApprovals';
import QuickPaymentErrors from '../components/admin/QuickPaymentErrors';

const AdminDashboard = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminUser, setAdminUser] = useState(null);

  const [lastViewed, setLastViewed] = useState(() => {
    try {
      const saved = localStorage.getItem('admin_last_viewed');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  const [badgeCounts, setBadgeCounts] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem('admin_last_viewed', JSON.stringify(lastViewed));
  }, [lastViewed]);

  useEffect(() => {
    // 管理者認証チェック
    const adminToken = localStorage.getItem('admin_token') || localStorage.getItem('token');
    const adminData = localStorage.getItem('admin_user') || localStorage.getItem('user');

    if (!adminToken || !adminData) {
      navigate('/login');
      return;
    }

    try {
      const parsedData = JSON.parse(adminData);
      if (parsedData.user_type !== 'admin' && parsedData.role !== 'admin') {
        navigate('/login');
        return;
      }
      setAdminUser(parsedData);

      // Initial fetch
      fetchSidebarBadges(parsedData);

      // Auto-refresh badges every 30s
      const timer = setInterval(() => fetchSidebarBadges(parsedData), 30000);
      return () => clearInterval(timer);
    } catch (error) {
      navigate('/login');
    }
  }, [navigate]);

  // Hook to refetch when lastViewed changes
  useEffect(() => {
    if (adminUser) {
      fetchSidebarBadges(adminUser);
    }
  }, [lastViewed]);

  const fetchSidebarBadges = async (currentUser) => {
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      if (!token) return;

      const payload = {
        users: lastViewed['users'] || null,
        jobs: lastViewed['jobs'] || null,
        reports: lastViewed['reports'] || null,
        notifications: lastViewed['notifications'] || null,
        identity: lastViewed['identity-verification'] || null,
        qualifications: lastViewed['qualification-management'] || null,
        wallet: lastViewed['wallet'] || null,
        payments: lastViewed['payments'] || null
      };

      const res = await axios.post(`${API_BASE}/admin/badges`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setBadgeCounts(res.data || {});
    } catch (e) {
      console.error(e);
    }
  };

  const handleMenuClick = (id) => {
    setActiveSection(id);

    // Update last viewed time to NOW
    const now = new Date().toISOString();
    setLastViewed(prev => ({
      ...prev,
      [id]: now
    }));

    if (window.innerWidth < 1024) setSidebarOpen(false);
  };

  const handleQuickNavigate = (section) => {
    setActiveSection(section);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('ログアウトしました');
    navigate('/admin-login');
  };

  const menuItems = [
    { id: 'overview', label: 'ダッシュボード', icon: <HomeIcon className="w-5 h-5" /> },
    { id: 'users', label: 'ユーザー管理', icon: <UsersIcon className="w-5 h-5" />, badge: badgeCounts['users'] },
    { id: 'identity-verification', label: '本人確認管理', icon: <IdentificationIcon className="w-5 h-5" />, badge: badgeCounts['identity-verification'] },
    { id: 'qualification-management', label: '資格承認管理', icon: <AcademicCapIcon className="w-5 h-5" />, badge: badgeCounts['qualification-management'] },
    { id: 'jobs', label: '求人・応募管理', icon: <BriefcaseIcon className="w-5 h-5" />, badge: badgeCounts['jobs'] },
    { id: 'categories', label: 'カテゴリ管理', icon: <TagIcon className="w-5 h-5" /> },
    { id: 'content-edit', label: 'コンテンツ編集', icon: <DocumentTextIcon className="w-5 h-5" /> },
    { id: 'wallet', label: '運営ウォレット', icon: <WalletIcon className="w-5 h-5" />, badge: badgeCounts['wallet'] },
    { id: 'payments', label: '決済・請求管理', icon: <CreditCardIcon className="w-5 h-5" />, badge: badgeCounts['payments'] },
    { id: 'reports', label: '通報・問い合わせ', icon: <ExclamationTriangleIcon className="w-5 h-5" />, badge: badgeCounts['reports'] },
    { id: 'data-export', label: 'データエクスポート', icon: <ArrowRightOnRectangleIcon className="w-5 h-5 rotate-90" /> },
    { id: 'notifications', label: 'プッシュ通知', icon: <BellIcon className="w-5 h-5" />, badge: badgeCounts['notifications'] },
    { id: 'banners', label: '広告バナー管理', icon: <SparklesIcon className="w-5 h-5" /> },
    { id: 'api-keys', label: 'APIキー管理', icon: <KeyIcon className="w-5 h-5" /> },
    { id: 'settings', label: 'システム設定', icon: <CogIcon className="w-5 h-5" /> },
    { id: 'analytics', label: 'アナリティクス', icon: <ChartBarIcon className="w-5 h-5" /> },
    { id: 'security', label: 'セキュリティ', icon: <ShieldCheckIcon className="w-5 h-5" /> },
    { id: 'optional', label: 'オプション機能', icon: <SparklesIcon className="w-5 h-5" /> }
  ];

  const renderActiveComponent = () => {
    const handleBackToMenu = () => {
      handleMenuClick('overview');
    };

    switch (activeSection) {
      case 'overview':
        return <DashboardOverview onNavigate={handleQuickNavigate} badgeCounts={badgeCounts} />;
      case 'users':
        return <UserManagement onBack={handleBackToMenu} />;
      case 'identity-verification':
        return <IdentityVerificationManagement onBack={handleBackToMenu} />;
      case 'qualification-management':
        return <QualificationManagement onBack={handleBackToMenu} />;
      case 'jobs':
        return <JobManagement onBack={handleBackToMenu} />;
      case 'categories':
        return <CategoryManagement onBack={handleBackToMenu} />;
      case 'content-edit':
        return <ContentEditManagement onBack={handleBackToMenu} />;
      case 'wallet':
        return <AdminWalletManagement onBack={handleBackToMenu} />;
      case 'payments':
        return <PaymentManagement onBack={handleBackToMenu} />;
      case 'reports':
        return <AbuseReports onBack={handleBackToMenu} />;
      case 'data-export':
        return <DataExport onBack={handleBackToMenu} />;
      case 'notifications':
        return <NotificationManagement onBack={handleBackToMenu} />;
      case 'banners':
        return <BannerManagement onBack={handleBackToMenu} />;
      case 'api-keys':
        return <APIKeyManagement onBack={handleBackToMenu} />;
      case 'settings':
        return <SystemSettings onBack={handleBackToMenu} />;
      case 'analytics':
        return <Analytics onBack={handleBackToMenu} />;
      case 'security':
        return <SecurityManagement onBack={handleBackToMenu} />;
      case 'optional':
        return <OptionalFeatures onBack={handleBackToMenu} />;
      case 'quick-new-users':
        return <QuickNewUsers onBack={handleBackToMenu} />;
      case 'quick-reports':
        return <AbuseReports onBack={handleBackToMenu} />;
      case 'quick-job-approvals':
        return <QuickJobApprovals onBack={handleBackToMenu} />;
      case 'quick-payment-errors':
        return <QuickPaymentErrors onBack={handleBackToMenu} />;
      default:
        return <DashboardOverview onNavigate={handleQuickNavigate} />;
    }
  };

  if (!adminUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar - Conditional for Overview logic */}
      {activeSection === 'overview' && (
        <>
          {/* Mobile menu overlay */}
          {sidebarOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
          )}

          <div className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-white shadow-lg transition-all duration-300 ease-in-out flex flex-col flex-shrink-0 fixed lg:relative z-50 h-full lg:h-auto ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>

            {/* Logo */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              {sidebarOpen && (
                <div>
                  <h1 className="text-lg sm:text-xl font-bold text-blue-600">バイターズ</h1>
                  <p className="text-xs text-gray-500">運営管理システム</p>
                </div>
              )}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title={sidebarOpen ? "メニューを閉じる" : "メニューを開く"}
              >
                {sidebarOpen ? <XMarkIcon className="w-5 h-5 text-gray-600" /> : <Bars3Icon className="w-5 h-5 text-gray-600" />}
              </button>
            </div>

            {/* Navigation Menu */}
            <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleMenuClick(item.id)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors text-sm ${activeSection === item.id
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                  <div className="relative flex items-center">
                    {item.icon}
                    {item.badge > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center min-w-[18px] px-1 h-5 text-[10px] font-bold text-white bg-red-600 rounded-full">
                        {String(item.badge)}
                      </span>
                    )}
                    <span className={`${sidebarOpen ? 'ml-3' : 'hidden'}`}>{item.label}</span>
                  </div>
                </button>
              ))}
            </nav>

            {/* Admin Info & Logout */}
            <div className="border-t border-gray-200 p-4">
              {sidebarOpen && adminUser && (
                <div className="mb-3">
                  <p className="text-sm font-medium text-gray-900 truncate">{adminUser.name}</p>
                  <p className="text-xs text-gray-500 truncate">{adminUser.role === 'admin' ? '管理者' : adminUser.role}</p>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
                {sidebarOpen && <span className="ml-3">ログアウト</span>}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-4 sm:px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {/* Mobile menu button (only visible if sidebar is hidden/collapsed on mobile overview) */}
              {activeSection === 'overview' && (
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors mr-2"
                >
                  <Bars3Icon className="w-5 h-5 text-gray-600" />
                </button>
              )}
              <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900 truncate">
                {menuItems.find(item => item.id === activeSection)?.label || 'ダッシュボード'}
              </h2>
            </div>
            <div className="flex items-center space-x-4">
              {/* Last Login Info */}
              <div className="text-xs sm:text-sm text-gray-500 hidden sm:block">
                {(() => {
                  try {
                    const d = adminUser?.last_login ? new Date(adminUser.last_login) : new Date();
                    return `最終ログイン: ${d.toLocaleDateString('ja-JP')} ${d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}`;
                  } catch (e) { return ''; }
                })()}
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-4 sm:p-6">
          <div className="max-w-7xl mx-auto">
            {renderActiveComponent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;